/**
 * explorationAtoms - Source atoms for relationship discovery and loading state.
 *
 * These atoms track the status of discovering relationships for the current
 * exploration node and which relationship keys are currently being loaded.
 */

import { atom, type Atom, type PrimitiveAtom } from 'jotai';

import { ObjectsQueryResultAtom } from '../../../../../state/LabsAtoms';
import type { RelationshipInfo } from '../services/cypherQueryService';
import { isGraphQLQueryFailure, extractTraceId } from '../services/cypherQueryService';

// ============================================================================
// Types
// ============================================================================

export type DiscoveryStatus = 'idle' | 'discovering' | 'discovered' | 'error';

// ============================================================================
// Source atoms
// ============================================================================

/** Discovered relationships for the active exploration node — outgoing */
export const DiscoveredOutgoingAtom: PrimitiveAtom<RelationshipInfo[]> & {
	init: RelationshipInfo[];
} = atom<RelationshipInfo[]>([]);

/** Discovered relationships for the active exploration node — incoming */
export const DiscoveredIncomingAtom: PrimitiveAtom<RelationshipInfo[]> & {
	init: RelationshipInfo[];
} = atom<RelationshipInfo[]>([]);

/** Status of the current relationship discovery */
export const DiscoveryStatusAtom: PrimitiveAtom<DiscoveryStatus> & {
	init: DiscoveryStatus;
} = atom<DiscoveryStatus>('idle');

/** Error message for exploration/discovery failures */
export const ExplorationErrorAtom: PrimitiveAtom<string | null> & {
	init: string | null;
} = atom<string | null>(null);

/**
 * Set of relationship keys currently being loaded (fetched from the server).
 * Used to prevent duplicate clicks and to show loading placeholder nodes.
 * Keys are in the format: `${nodeAri}:${direction}:${relType}`
 */
export const LoadingRelKeysAtom: PrimitiveAtom<Set<string>> & {
	init: Set<string>;
} = atom<Set<string>>(new Set<string>());

/**
 * Tracks per-direction discovery failures and their associated trace IDs.
 *
 * When relationship discovery queries fail for a given direction (outgoing
 * or incoming), the raw responses are inspected for GraphQL-level errors.
 * If ALL queries for a direction fail, that direction is recorded here
 * along with the trace IDs from the failed responses so users can report
 * them for debugging.
 */
export type DirectionFailure = {
	/** The gateway trace IDs from failed responses for this direction. */
	traceIds: string[];
};

export type DiscoveryFailures = {
	outgoing: DirectionFailure | null;
	incoming: DirectionFailure | null;
};

/** Per-direction discovery failure state with trace IDs */
export const DiscoveryFailuresAtom: PrimitiveAtom<DiscoveryFailures> & {
	init: DiscoveryFailures;
} = atom<DiscoveryFailures>({
	outgoing: null,
	incoming: null,
});

// ============================================================================
// Derived atoms
// ============================================================================

/** Whether discovery is currently in progress */
export const IsDiscoveringAtom: Atom<boolean> = atom<boolean>((get) => {
	return get(DiscoveryStatusAtom) === 'discovering';
});

// ============================================================================
// Error flag atoms — derived from query result and discovery failure state.
//
// These atoms compute error flag data reactively from source atoms, following
// Jotai-first principles. The component simply reads the derived list; no
// useEffect / useRef deduplication is needed.
// ============================================================================

/** Shape of a single error flag for rendering */
export type ErrorFlagData = {
	id: string;
	title: string;
	description: string;
	appearance: 'error';
	traceIds?: string[];
};

const QUERY_ERROR_FLAG_ID = 'query-error';
const DISCOVERY_ERROR_FLAG_ID = 'discovery-error';

/**
 * Derives an error flag from the initial query result.
 *
 * Detects both:
 * - Fetch-level failures: `queryResult.error` (set by the catch block)
 * - GraphQL-level failures: `queryResult.errors` array with null graphStore
 *
 * Only produces a flag when no usable data was returned (total failure).
 */
export const QueryErrorFlagAtom: Atom<ErrorFlagData | null> = atom<ErrorFlagData | null>((get) => {
	const queryResult = get(ObjectsQueryResultAtom);
	if (!queryResult) {
		return null;
	}

	const hasFetchError = queryResult.error != null;
	const hasGraphQLError = isGraphQLQueryFailure(queryResult);

	if (!hasFetchError && !hasGraphQLError) {
		return null;
	}

	let description: string;
	let traceIds: string[] | undefined;

	if (hasFetchError) {
		description =
			typeof queryResult.error === 'string' ? queryResult.error : 'An unknown error occurred';
	} else {
		description = queryResult.errors?.[0]?.message ?? 'An unknown error occurred';
		const traceId = extractTraceId(queryResult);
		if (traceId) {
			traceIds = [traceId];
		}
	}

	return {
		id: QUERY_ERROR_FLAG_ID,
		title: 'Query Error',
		description,
		appearance: 'error' as const,
		traceIds,
	};
});

/**
 * Derives a direction-specific error flag from discovery failures.
 *
 * Messages:
 * - Both directions failed: "Something went wrong when fetching relationships"
 * - Only outgoing failed: "Something went wrong when fetching outgoing relationships"
 * - Only incoming failed: "Something went wrong when fetching incoming relationships"
 */
export const DiscoveryErrorFlagAtom: Atom<ErrorFlagData | null> = atom<ErrorFlagData | null>(
	(get) => {
		const { outgoing: outFail, incoming: inFail } = get(DiscoveryFailuresAtom);

		if (!outFail && !inFail) {
			return null;
		}

		let description: string;
		const allTraceIds: string[] = [];

		if (outFail && inFail) {
			description = 'Something went wrong when fetching relationships';
			allTraceIds.push(...outFail.traceIds, ...inFail.traceIds);
		} else if (outFail) {
			description = 'Something went wrong when fetching outgoing relationships';
			allTraceIds.push(...outFail.traceIds);
		} else {
			description = 'Something went wrong when fetching incoming relationships';
			allTraceIds.push(...inFail!.traceIds);
		}

		return {
			id: DISCOVERY_ERROR_FLAG_ID,
			title: 'Relationship Error',
			description,
			appearance: 'error' as const,
			traceIds: allTraceIds.length > 0 ? allTraceIds : undefined,
		};
	},
);

/**
 * Combined list of all active error flags.
 *
 * Components read this single atom and render the flags. Dismissal is handled
 * separately via local component state (a set of dismissed IDs), since
 * dismissal is a transient UI concern that should not propagate back into
 * the source atoms.
 */
export const ErrorFlagsAtom: Atom<ErrorFlagData[]> = atom<ErrorFlagData[]>((get) => {
	const flags: ErrorFlagData[] = [];
	const queryFlag = get(QueryErrorFlagAtom);
	if (queryFlag) {
		flags.push(queryFlag);
	}
	const discoveryFlag = get(DiscoveryErrorFlagAtom);
	if (discoveryFlag) {
		flags.push(discoveryFlag);
	}
	return flags;
});
